import React from 'react';
import PropTypes from 'prop-types';
import {
  MdCategory, MdStorefront, MdPublic,
  MdPhoneIphone, MdMailOutline, MdBadge, MdErrorOutline,
} from 'react-icons/md';
import ProjectLinkRenderer from "./ProjectLinkRenderer";
import NumberRenderer from "components/common/ag-grid/NumberRenderer";
import { CustomerCell, EndUserCell, ContractorCell, ConsultantCell } from "./CompanyCellRenderer";
import { CreatedByCell, SalesInchargeCell, ContactCell } from "./PersonCellRenderer";
import ProjectCompetitorRenderer from "./ProjectCompetitorRenderer";
import ProjectLocationRenderer from "./ProjectLocationRenderer";
import ProjectSuccessRenderer from "./ProjectSuccessRenderer";
import ProjectStatusRenderer from "./ProjectStatusRenderer";
import PJNumberRenderer from "./PJNumberRenderer";
import AmountUSDRenderer from "components/common/ag-grid/AmountUSDRenderer";

// A muted leading icon per cell for the descriptive columns. The icon repeats
// down the column on purpose: with twenty-plus columns on this grid, the mark
// is what tells a reader mid-scroll which column they are in when the header
// has left the screen.
const iconCell = (Icon, iconClass = 'tw-text-slate-400') => {
  const Renderer = ({ value }) => {
    if (value == null || value === '') return null;
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full">
        <Icon size={13} className={`${iconClass} tw-shrink-0`} />
        <span className="tw-truncate">{value}</span>
      </span>
    );
  };
  Renderer.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };
  return Renderer;
};

const SORTABLE_COLS = [
  { headerName: 'PCV number',    field: 'pcv_number',  minWidth: 150, sortable: true, cellRenderer: ProjectLinkRenderer },
  { headerName: 'Project name',  field: 'name',        minWidth: 200, sortable: true, cellRenderer: ProjectLinkRenderer },
  { headerName: 'Quote number',  field: 'q_number',    minWidth: 130, sortable: true },
  { headerName: 'Status',        field: 'sales_pipeline.name', minWidth: 150, cellRenderer: ProjectStatusRenderer },
  { headerName: 'Customer name', field: 'customer.name',          minWidth: 200, cellRenderer: CustomerCell },
  { headerName: 'Customer type', field: 'customer.type_of_customer', minWidth: 130, cellRenderer: iconCell(MdCategory) },
  { headerName: 'Building type 1', field: 'building_type_l1.name', minWidth: 150 },
  { headerName: 'Building type 2', field: 'building_type_l2.name', minWidth: 130 },
  { headerName: 'Enduser',         field: 'enduser.name',           minWidth: 200, cellRenderer: EndUserCell },
  { headerName: "Enduser's industry", field: 'enduser.industry',   minWidth: 200 },
  { headerName: 'Contractor',    field: 'contractor.name', minWidth: 200, cellRenderer: ContractorCell },
  { headerName: 'Consultant',    field: 'consultant.name', minWidth: 200, cellRenderer: ConsultantCell },
  { headerName: 'Competitors',   field: 'competitors',     minWidth: 130, cellRenderer: ProjectCompetitorRenderer },
  { headerName: 'Project location', field: 'pj_location',  minWidth: 200, cellRenderer: ProjectLocationRenderer },
  { headerName: 'Province',      field: 'pj_province',     minWidth: 150, sortable: true },
  { headerName: 'Amount',        field: 'amount',           minWidth: 130, sortable: true, cellRenderer: AmountUSDRenderer },
  { headerName: 'Weight (M.T)',  field: 'weight',           minWidth: 130, sortable: true, cellRenderer: NumberRenderer },
  { headerName: 'Area (m2)',     field: 'area',             minWidth: 130, sortable: true, cellRenderer: NumberRenderer },
  { headerName: 'Expected month signing contract', field: 'expected_date_of_signing_contract', minWidth: 180, sortable: true },
  { headerName: 'Sales incharge', field: 'sales_incharge', minWidth: 150, sortable: true, cellRenderer: SalesInchargeCell },
  { headerName: 'Sales unit',    field: 'sales_unit.full_name', minWidth: 180, cellRenderer: iconCell(MdStorefront) },
  { headerName: 'Sales region',  field: 'sales_unit.region',    minWidth: 180, cellRenderer: iconCell(MdPublic) },
  { headerName: 'Mode',          field: 'project_mode',         minWidth: 80 },
  { headerName: 'Success',       field: 'percent_success',      minWidth: 120, cellRenderer: ProjectSuccessRenderer },
  { headerName: 'Contact name',  field: 'contact.name',         minWidth: 180, cellRenderer: ContactCell },
  { headerName: 'Mobile',        field: 'contact.mobile',       minWidth: 120, cellRenderer: iconCell(MdPhoneIphone) },
  { headerName: 'Email',         field: 'contact.email',        minWidth: 180, cellRenderer: iconCell(MdMailOutline) },
  { headerName: 'Position',      field: 'contact.position',     minWidth: 140, cellRenderer: iconCell(MdBadge) },
  {
    headerName: 'Lost reason',
    field: 'lost_reason',
    minWidth: 200,
    // The icon shares the text's red: this cell is a warning, not a label.
    cellRenderer: (params) => (params.value ? (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full tw-text-red-600">
        <MdErrorOutline size={13} className="tw-shrink-0" />
        <span className="tw-truncate">{params.value}</span>
      </span>
    ) : null),
  },
  {
    headerName: 'Project type',
    field: 'project_type',
    minWidth: 120,
    cellRenderer: (params) => {
      const isHotRoll = params.value === 'hot_roll';
      return (
        <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${isHotRoll ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-primary-100 tw-text-primary-700'}`}>
          {isHotRoll ? 'Hot Roll' : 'Built Up'}
        </span>
      );
    },
  },
  { headerName: 'Created by',   field: 'created_by',   minWidth: 150, cellRenderer: CreatedByCell },
  { headerName: 'Created date', field: 'created_date', minWidth: 150, sortable: true },
];

export const jobColumnDefs = [
  ...SORTABLE_COLS.slice(0, 3),
  { headerName: 'P/J number', field: 'j_number', minWidth: 110, sortable: true, cellRenderer: PJNumberRenderer },
  ...SORTABLE_COLS.slice(3),
];

export const pcvColumnDefs = SORTABLE_COLS;
